import { useEffect, useState } from "react";
import { getLegalTextsApiPath } from "../utils/publicSiteConfigPaths";
import { isLegalPlaceholderContent } from "../utils/isLegalPlaceholderContent";

export const LEGAL_TEXT_DEFAULTS = {
  terminosHtml: "",
  privacidadHtml: "",
  terminosUpdatedLabel: "Última actualización: Febrero 2026 (v1)",
  privacidadUpdatedLabel: "Última actualización: Febrero 2026 (v1)",
};

function publishedLegalHtml(html) {
  // Fiscal markers are replaced by the API. Public pages keep that HTML.
  return isLegalPlaceholderContent(html) ? "" : html.trim();
}

function mergeLegalTexts(data) {
  const merged = { ...LEGAL_TEXT_DEFAULTS };
  if (!data || typeof data !== "object") {
    return merged;
  }
  for (const key of Object.keys(LEGAL_TEXT_DEFAULTS)) {
    const val = data[key];
    if (typeof val === "string" && val.trim()) {
      merged[key] = val.trim();
    }
  }
  return merged;
}

/**
 * @param {"terminos" | "privacidad"} page
 */
export default function useLegalPageContent(page) {
  const [contentHtml, setContentHtml] = useState("");
  const [updatedLabel, setUpdatedLabel] = useState("");
  const [prestador, setPrestador] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        // Public pages must stay anonymous. A signed-in director would otherwise
        // receive the raw template with {{razonSocial}} markers instead of the
        // substituted fiscal text. The legal-text editor keeps sending the session.
        const res = await fetch(getLegalTextsApiPath(), {
          credentials: "omit",
          signal: controller.signal,
        });
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        const merged = mergeLegalTexts(data);
        if (controller.signal.aborted) {
          return;
        }
        const nextPrestador =
          data.prestador && typeof data.prestador === "object" && !Array.isArray(data.prestador)
            ? data.prestador
            : null;
        setPrestador(nextPrestador);
        if (page === "terminos") {
          setContentHtml(publishedLegalHtml(merged.terminosHtml));
          setUpdatedLabel(merged.terminosUpdatedLabel);
        } else {
          setContentHtml(publishedLegalHtml(merged.privacidadHtml));
          setUpdatedLabel(merged.privacidadUpdatedLabel);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          setContentHtml("");
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    })();
    return () => controller.abort();
  }, [page]);

  return { contentHtml, updatedLabel, prestador, loading };
}
